import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/AdminLayout.css';

const AdminRoute = ({ children }) => {
  const { loading } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Get or create a tab ID for this session
    // This helps track if session belongs to this tab, but we'll be more lenient on refresh
    let tabId = sessionStorage.getItem('adminTabId');
    if (!tabId) {
      // First time in this tab - generate new ID
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('adminTabId', tabId);
    }
    
    // Check admin session from sessionStorage
    // Using sessionStorage so session expires when tab is closed
    const adminSession = sessionStorage.getItem('adminSession');
    const sessionTimestamp = sessionStorage.getItem('adminSessionTimestamp');
    const sessionTabId = sessionStorage.getItem('adminSessionTabId');
    
    // Verify the session is valid
    if (adminSession === 'true' && sessionTimestamp) {
      // Check timestamp validity first
      const now = Date.now();
      const timestamp = parseInt(sessionTimestamp, 10);
      
      if (now - timestamp < 24 * 60 * 60 * 1000) {
        // Session timestamp is valid
        // If sessionTabId exists and doesn't match, it might be from a different tab
        // But if sessionTabId doesn't exist, it's an old format - we'll accept it and update it
        if (sessionTabId && sessionTabId !== tabId) {
          // Session is from a different tab - don't allow it
          sessionStorage.removeItem('adminSession');
          sessionStorage.removeItem('adminSessionTimestamp');
          sessionStorage.removeItem('adminSessionTabId');
          setIsAuthenticated(false);
        } else {
          // Session is valid - update tab ID if it wasn't set
          if (!sessionTabId) {
            sessionStorage.setItem('adminSessionTabId', tabId);
          }
          setIsAuthenticated(true);
        }
      } else {
        // Session expired
        sessionStorage.removeItem('adminSession');
        sessionStorage.removeItem('adminSessionTimestamp');
        sessionStorage.removeItem('adminSessionTabId');
        setIsAuthenticated(false);
      }
    } else {
      // No valid session - clear any stale data
      if (adminSession || sessionTimestamp || sessionTabId) {
        sessionStorage.removeItem('adminSession');
        sessionStorage.removeItem('adminSessionTimestamp');
        sessionStorage.removeItem('adminSessionTabId');
      }
      setIsAuthenticated(false);
    }
    
    setIsChecking(false);

    // Note: We're NOT clearing sessionStorage on beforeunload/pagehide anymore
    // because these events fire on refresh too, not just tab close
    // sessionStorage will naturally persist across page refreshes within the same tab
    // and will only clear when the tab is actually closed
  }, []);

  // Show loading state while checking authentication
  if (isChecking || loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f5f7fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div 
            className="admin-spinner"
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              margin: '0 auto 1rem'
            }}
          ></div>
          <p style={{ color: '#6b7280' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // If no admin session exists, redirect to login immediately
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Ensure children is valid before rendering
  if (!children) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

export default AdminRoute;

